import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import MyCartSidebar from '../webdesigned/MyCartSidebar';
import CheckoutForm from '../webdesigned/CheckoutForm';
import GuestReservationForm from '../webdesigned/GuestReservationForm';
import WebDesignedClerkAuthPanel from '../webdesigned/WebDesignedClerkAuthPanel';
import type { WebDesignedCartContextValue } from '../../contexts/WebDesignedCartContext';
import type { User as AppUser } from '../../types';

/** Checkout — same floating cream shell as cart on phone; wider right panel on pad/desktop. */
const checkoutPanelClass =
  'fixed z-[10050] flex flex-col overflow-hidden bg-white shadow-2xl ' +
  'rounded-2xl border border-black/15 ' +
  'left-2 right-2 top-[calc(env(safe-area-inset-top,0px)+0.75rem)] ' +
  'bottom-[calc(env(safe-area-inset-bottom,0px)+5.25rem)] ' +
  'min-[744px]:left-auto min-[744px]:right-0 min-[744px]:top-[var(--wd-panel-top)] ' +
  'min-[744px]:bottom-0 min-[744px]:w-full min-[744px]:max-w-none min-[744px]:rounded-none ' +
  'min-[744px]:border-0 min-[744px]:border-l min-[744px]:border-gray-200 ' +
  'min-[744px]:sm:w-96 md:w-[32rem] lg:w-[36rem] xl:w-[40rem]';

/**
 * Login/auth shell — same floating inset geometry as calendar assistant on all viewports,
 * mirrored on the right. Phone leaves a bottom gap so the dock (language) stays usable.
 */
/** Above checkout (z-10050 on phone) so Sign in from checkout is reachable. */
const authPanelClass =
  'fixed z-[10060] flex flex-col overflow-hidden bg-[#ece3de] shadow-2xl ' +
  'rounded-2xl border border-black/15 ' +
  'left-2 right-2 top-[calc(env(safe-area-inset-top,0px)+0.75rem)] ' +
  'bottom-[calc(env(safe-area-inset-bottom,0px)+5.25rem)] ' +
  'min-[744px]:left-auto min-[744px]:right-3 min-[744px]:w-[22rem] ' +
  'min-[744px]:bottom-[calc(env(safe-area-inset-bottom,0px)+8px)] ' +
  'min-[1367px]:right-4 min-[1367px]:w-[26rem] min-[1367px]:bottom-8';

/** Quick reservation — same floating inset shell as auth, slightly wider for the calendar. */
const reservationPanelClass =
  'fixed z-[10020] flex flex-col overflow-hidden bg-[#ece3de] shadow-2xl ' +
  'rounded-2xl border border-black/15 ' +
  'left-2 right-2 top-[calc(env(safe-area-inset-top,0px)+0.75rem)] ' +
  'bottom-[calc(env(safe-area-inset-bottom,0px)+5.25rem)] ' +
  'min-[744px]:left-auto min-[744px]:right-3 min-[744px]:w-[24rem] ' +
  'min-[744px]:bottom-[calc(env(safe-area-inset-bottom,0px)+8px)] ' +
  'min-[1367px]:right-4 min-[1367px]:w-[28rem] min-[1367px]:bottom-8';

/** My Cart (dock) — same floating cream shell as login / quick reservation. */
const cartPanelClass = reservationPanelClass;

export type WebDesignedPortalSlideOutsProps = {
  cart: WebDesignedCartContextValue;
  headerOffsetPx: number;
  user: AppUser | null;
  onReservationFormSubmit: (reservationData: unknown) => void;
  onCheckoutReservationSubmit: (reservationData: unknown) => void;
};

/**
 * Cart, checkout, quick reservation, and auth panels.
 * Cart/auth/reservation/checkout: floating inset shells on phone (dock stays usable).
 * Pad/desktop checkout: slide-from-right panel.
 */
const WebDesignedPortalSlideOuts: React.FC<WebDesignedPortalSlideOutsProps> = ({
  cart,
  headerOffsetPx,
  user,
  onReservationFormSubmit,
  onCheckoutReservationSubmit,
}) => {
  const panelStyle = {
    '--wd-panel-top': `${headerOffsetPx}px`,
  } as React.CSSProperties;

  const sheetLocksPageScroll =
    cart.cartSidebarOpen ||
    cart.checkout.isOpen ||
    cart.reservationForm.isOpen ||
    cart.authPanelOpen;

  // Soft lock so background page doesn’t steal taps while completing book/checkout.
  useEffect(() => {
    if (!sheetLocksPageScroll) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverscroll = html.style.overscrollBehavior;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overscrollBehavior = prevHtmlOverscroll;
      body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, [sheetLocksPageScroll]);

  return (
    <>
      <AnimatePresence initial={false}>
        {cart.cartSidebarOpen && (
          <motion.div
            key="web-cart-sidebar"
            data-keep-web-nav-open="true"
            className={cartPanelClass}
            onClick={(e) => e.stopPropagation()}
            initial={{ x: '100%', opacity: 0.98 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.98 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
          >
            <MyCartSidebar
              isOpen={cart.cartSidebarOpen}
              onClose={() => cart.setCartSidebarOpen(false)}
              shoppingCartData={cart.shoppingCartData}
              onUpdateShoppingCartData={(data) => cart.setShoppingCartData(data)}
              currentUser={user}
              onCheckout={(selectedItems, shoppingCartData, paymentMode) => {
                if (selectedItems.length > 0) {
                  cart.openCheckout(selectedItems, shoppingCartData ?? null, paymentMode ?? 'guest');
                }
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {cart.checkout.isOpen && (
          <motion.div
            key="web-checkout-sidebar"
            data-keep-web-nav-open="true"
            className={checkoutPanelClass}
            style={panelStyle}
            onClick={(e) => e.stopPropagation()}
            initial={{ x: '100%', opacity: 0.98 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.98 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
          >
            <CheckoutForm
              isOpen={cart.checkout.isOpen}
              onClose={cart.closeCheckout}
              selectedItems={cart.checkout.selectedItems}
              shoppingCartData={cart.checkout.shoppingCartData ?? undefined}
              paymentMode={cart.checkout.paymentMode}
              currentUser={user}
              onReservationSubmit={onCheckoutReservationSubmit}
              onOpenAuth={cart.openAuthPanel}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {cart.reservationForm.isOpen && (
          <motion.div
            key="web-reservation-sidebar"
            data-keep-web-nav-open="true"
            className={reservationPanelClass}
            onClick={(e) => e.stopPropagation()}
            initial={{ x: '100%', opacity: 0.98 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.98 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
          >
            <GuestReservationForm
              isOpen={cart.reservationForm.isOpen}
              onClose={cart.closeReservationForm}
              itemType={cart.reservationForm.itemType}
              itemId={cart.reservationForm.itemId}
              itemName={cart.reservationForm.itemName}
              itemPrice={cart.reservationForm.itemPrice}
              taxeHebergementEnabled={cart.reservationForm.taxeHebergementEnabled}
              onReservationSubmit={onReservationFormSubmit}
              shoppingCartData={cart.reservationForm.shoppingCartData ?? cart.shoppingCartData ?? undefined}
              currentUser={user}
              initialPaymentMode={cart.reservationForm.paymentMode}
              onDateChange={(startDate, endDate) => {
                const prev = cart.shoppingCartData;
                cart.setShoppingCartData({
                  ...(prev ?? {}),
                  startDate: startDate ?? prev?.startDate ?? null,
                  endDate: endDate ?? prev?.endDate ?? null,
                  groups: prev?.groups ?? [],
                });
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {cart.authPanelOpen && (
          <motion.div
            key="web-auth-sidebar"
            data-keep-web-nav-open="true"
            className={authPanelClass}
            onClick={(e) => e.stopPropagation()}
            initial={{ x: '100%', opacity: 0.98 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0.98 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
          >
            <WebDesignedClerkAuthPanel
              isOpen={cart.authPanelOpen}
              onClose={() => cart.setAuthPanelOpen(false)}
              initialMode={cart.authPanelMode}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WebDesignedPortalSlideOuts;
